/**
 * Redacts secrets from the URLs and URL-bearing text the production Sentry client sends.
 *
 * Sign-in callbacks (/auth/callback/<provider>) hold access and refresh tokens in the
 * fragment, or the query of the older API, until the page reads and clears them; emailed
 * links carry ?token=, OAuth returns carry code and state, WebSocket and image URLs carry
 * ?token=<JWT>, and invitation links carry the token as a path segment. Sentry copies the
 * page URL into every event and page-load trace, and every fetch, XHR and navigation into
 * a breadcrumb or span, so each of those is scrubbed here before it leaves the browser.
 *
 * The rules mirror mostlyagent/common/services/sentry_scrubbing.py; keep the two in step.
 */
import type { Breadcrumb, Event, StackFrame } from '@sentry/react';

export const FILTERED = '[Filtered]';

// URL parameters and request fields, matched case-insensitively with and without
// separators (api-key is apikey). code, state, key and session are too common as plain
// names to hide everywhere, so only URLs, query maps and headers use these.
const SENSITIVE_KEYS = new Set([
  'access_token', 'refresh_token', 'id_token', 'token', 'code', 'state', 'jwt', 'password', 'secret',
  'api_key', 'apikey', 'key', 'signature', 'sig', 'auth', 'authorization', 'session', 'sessionid',
]);
// Names that hold a secret wherever they appear (span and breadcrumb data, tags, quoted
// fields in text): accessToken, client_secret, X-Amz-Signature, x-api-key, Cookie.
const SECRET_SUFFIX = /(?:token|secret|password|passwd|signature|apikey|privatekey)$/;
const SECRET_NAMES = new Set(['jwt', 'authorization', 'proxyauthorization', 'cookie', 'setcookie', 'sessionid']);

// A key=value after ?, &, # or ; (raw or percent-encoded), or at the start of the text or
// of a quoted literal, so a bare query string counts too. Values stop at the next raw
// delimiter, so a sensitive value is redacted whole; a non-sensitive value is scanned
// again for an encoded URL nested inside it (redirect=%2Fx%3Ftoken%3D...).
const PARAM = /(^(?:b?['"])?|[?&#;]|%3[Ff]|%26|%23|%3[Bb])([^=&#?;%\s"'<>/]+)(=|%3[Dd])([^&#?;\s"'<>]*)/g;
// 'refresh_token': '...' in a repr, "refresh_token": "..." in JSON text.
const QUOTED_PAIR = /(['"])([\w.-]+)\1(\s*[:=]\s*)(['"])((?:(?!\4).)*)\4/g;
// Paths whose next segment is a bearer token: /invite/<t>, /api/v1/invitations/<t>, ...
// A route template (/invite/:token, /invitations/{token}) is left as it is.
const TOKEN_PARENTS = ['invite', 'organization-invite', 'claim-share', 'invitations', 'gmail-invites/t', 'push/clicked'];
const TOKEN_PATH = new RegExp(`(/(?:${TOKEN_PARENTS.join('|')})/)([^/?#\\s"'<>:{[][^/?#\\s"'<>]*)`, 'gi');
// A JWT (or any base64url JSON) anywhere: its header always encodes to eyJ.
const JWT = /(^|[^A-Za-z0-9_-])eyJ[A-Za-z0-9_-]{10,}(?:\.[A-Za-z0-9_-]+){0,2}/g;
const BEARER = /\b(Bearer\s+)[A-Za-z0-9._~+/-]{8,}=*/gi;
const HEADER_ATTRIBUTE = /^http\.(?:request|response)\.header\.(.+)$/;

type Fields = Record<string, unknown>;

const compact = (key: string): string => key.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

/** Whether a field of this name holds a secret in any structure. */
export const isSecretName = (key: string): boolean => SECRET_NAMES.has(compact(key))
  || SECRET_SUFFIX.test(compact(key));

/** Whether a URL parameter, request header or query field of this name holds a secret. */
export const isSensitiveKey = (key: string): boolean => SENSITIVE_KEYS.has(key.trim().toLowerCase())
  || SENSITIVE_KEYS.has(compact(key)) || isSecretName(key);

const scrubParams = (text: string): string => text.replace(
  PARAM,
  (match: string, delimiter: string, key: string, equals: string, value: string) => {
    if (!value) return match;

    return `${delimiter}${key}${equals}${isSensitiveKey(key) ? FILTERED : scrubParams(value)}`;
  },
);

const scrubQuotedPair = (
  match: string, quote: string, key: string, separator: string, valueQuote: string, value: string,
): string => (value && isSecretName(key) ? `${quote}${key}${quote}${separator}${valueQuote}${FILTERED}${valueQuote}` : match);

/**
 * The URL (or any text holding URLs) with sensitive query and fragment values (a bare query
 * string included), quoted secret fields, token path segments, JWTs and bearer credentials
 * replaced by [Filtered]. Never throws: the input is matched as text, not parsed, so a
 * malformed URL comes back with only those parts redacted.
 */
export const scrubUrl = <T>(value: T): T => {
  if (typeof value !== 'string' || !value) return value;

  return scrubParams(value)
    .replace(QUOTED_PAIR, scrubQuotedPair)
    .replace(TOKEN_PATH, `$1${FILTERED}`)
    .replace(JWT, `$1${FILTERED}`)
    .replace(BEARER, `$1${FILTERED}`) as unknown as T;
};

/** Scrubs target[key] in place when it holds a string; leaves an absent field absent. */
const scrubString = <K extends string>(target: Partial<Record<K, unknown>>, key: K): void => {
  if (typeof target[key] === 'string') target[key] = scrubUrl(target[key]);
};

const isFields = (value: unknown): value is Fields => typeof value === 'object' && value !== null
  && !Array.isArray(value);

const scrubValue = (value: unknown): unknown => (
  Array.isArray(value) ? value.map((item) => scrubUrl(item)) : scrubUrl(value)
);

/** A copy of span or breadcrumb data: secret fields and header attributes filtered, URLs scrubbed. */
const scrubData = (data: Fields): Fields => {
  const scrubbed: Fields = {};

  Object.keys(data).forEach((key) => {
    const header = HEADER_ATTRIBUTE.exec(key)?.[1];
    const secret = header ? isSensitiveKey(header) : isSecretName(key);

    scrubbed[key] = secret ? FILTERED : scrubValue(data[key]);
  });

  return scrubbed;
};

const scrubFields = (fields: Fields, isSensitive: (key: string) => boolean): Fields => {
  Object.keys(fields).forEach((key) => {
    fields[key] = isSensitive(key) ? FILTERED : scrubValue(fields[key]);
  });

  return fields;
};

type QueryString = NonNullable<NonNullable<Event['request']>['query_string']>;

const scrubQuery = (query: QueryString): QueryString => {
  if (typeof query === 'string') return scrubUrl(query);
  if (Array.isArray(query)) {
    return query.map(([key, value]): [string, string] => [key, isSensitiveKey(key) ? FILTERED : scrubUrl(value)]);
  }

  return scrubFields(query, isSensitiveKey) as QueryString;
};

const scrubRequest = (request: NonNullable<Event['request']>): void => {
  scrubString(request, 'url');
  if (request.query_string) request.query_string = scrubQuery(request.query_string);
  if (isFields(request.headers)) scrubFields(request.headers, isSensitiveKey);
  if (isFields(request.cookies)) scrubFields(request.cookies, () => true);
};

const scrubFrame = (frame: StackFrame): void => {
  scrubString(frame, 'filename');
  scrubString(frame, 'abs_path');
};

type SpanLike = { description?: string; data?: Fields };

const scrubSpan = (span: SpanLike): void => {
  scrubString(span, 'description');
  if (isFields(span.data)) span.data = scrubData(span.data);
};

const scrubBreadcrumbFields = (breadcrumb: Breadcrumb): Breadcrumb => ({
  ...breadcrumb,
  ...(breadcrumb.message ? { message: scrubUrl(breadcrumb.message) } : {}),
  ...(isFields(breadcrumb.data) ? { data: scrubData(breadcrumb.data) } : {}),
});

const scrubExceptions = (event: Event): void => {
  (event.exception?.values ?? []).forEach((exception) => {
    scrubString(exception, 'value');
    (exception.stacktrace?.frames ?? []).forEach(scrubFrame);
  });
};

const scrubTrace = (event: Event): void => {
  const trace = event.contexts?.trace as SpanLike | undefined;

  if (trace) scrubSpan(trace);
  (event.spans ?? []).forEach((span) => scrubSpan(span as SpanLike));
};

const scrubTrail = (event: Event): void => {
  if (isFields(event.tags)) scrubFields(event.tags, isSecretName);
  if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumbFields);
};

const scrubEventFields = (event: Event): Event => {
  if (event.request) scrubRequest(event.request);
  scrubString(event, 'transaction');
  scrubString(event, 'message');
  scrubExceptions(event);
  scrubTrace(event);
  scrubTrail(event);

  return event;
};

/** The inner scrubbers, reached through this object so a test can make them fail. */
export const sentryScrubbers = { event: scrubEventFields, breadcrumb: scrubBreadcrumbFields };

// What survives when scrubbing fails (see stripEvent).
const STRIPPED_FIELDS = ['request', 'breadcrumbs', 'spans', 'extra', 'contexts', 'logentry'];
const KEPT_TRACE_FIELDS = ['trace_id', 'span_id', 'parent_span_id', 'op', 'status'];
const KEPT_FRAME_FIELDS = ['filename', 'abs_path', 'module', 'function', 'lineno', 'colno', 'in_app'];
const BARE_EVENT_FIELDS = ['event_id', 'timestamp', 'start_timestamp', 'type', 'level', 'platform', 'environment'];
const SCRUB_FAILED_TAG = { sentry_scrub: 'failed' };

type ExceptionValue = NonNullable<NonNullable<Event['exception']>['values']>[number];

/** scrubUrl(value), or undefined when the scrubber itself fails. */
const safeText = (value: unknown): unknown => {
  try {
    return scrubUrl(value);
  } catch {
    return undefined;
  }
};

/** Scrubs fields[key] with the plain text rules; drops it when that fails or it is not text. */
const keepText = (fields: Fields, key: string): void => {
  const scrubbed = typeof fields[key] === 'string' ? safeText(fields[key]) : undefined;

  if (scrubbed === undefined) delete fields[key];
  else fields[key] = scrubbed;
};

const pick = (source: unknown, keys: string[]): Fields => {
  const kept: Fields = {};

  if (isFields(source)) keys.filter((key) => key in source).forEach((key) => { kept[key] = source[key]; });

  return kept;
};

const stripFrame = (frame: unknown): Fields => {
  const kept = pick(frame, KEPT_FRAME_FIELDS);

  keepText(kept, 'filename');
  keepText(kept, 'abs_path');

  return kept;
};

const stripException = (exception: ExceptionValue): Fields => {
  const kept = pick(exception, ['type', 'module', 'value']);
  const frames = exception.stacktrace?.frames;

  keepText(kept, 'value');
  if (Array.isArray(frames)) kept.stacktrace = { frames: frames.map(stripFrame) };

  return kept;
};

const stripStructure = (fields: Fields, event: Event): void => {
  const trace = pick(event.contexts?.trace, KEPT_TRACE_FIELDS);
  const values = event.exception?.values;

  STRIPPED_FIELDS.forEach((field) => { delete fields[field]; });
  if (Object.keys(trace).length) fields.contexts = { trace };
  if (Array.isArray(values)) fields.exception = { values: values.map(stripException) };
};

const stripText = (fields: Fields): void => {
  const tags = isFields(fields.tags) ? fields.tags : {};
  const kept: Fields = {};

  ['message', 'transaction'].forEach((field) => keepText(fields, field));
  Object.keys(tags).forEach((key) => {
    const value = safeText(tags[key]);

    if (value !== undefined) kept[key] = value;
  });
  fields.tags = { ...kept, ...SCRUB_FAILED_TAG };
};

/**
 * The event without anything the scrubber failed to vouch for: no request, breadcrumbs,
 * spans, extra or contexts (but the trace ids a transaction needs); exception types and
 * frame files and functions stay, with text scrubbed by the plain rules or dropped.
 */
const stripEvent = <T extends Event>(event: T): T => {
  const fields = event as unknown as Fields;

  stripStructure(fields, event);
  stripText(fields);

  return event;
};

/**
 * beforeSend and beforeSendTransaction: the page URL, Referer and query in `request`, the
 * transaction name, message and exception text, stack frame URLs (an inline script's frame
 * is the page URL), tags, recorded breadcrumbs, and every span's description and URL data.
 * Sentry drops an event whose hook throws, so a scrubber failure sends the event stripped
 * instead (tagged sentry_scrub: failed), and failing that only its bare identity.
 */
export const scrubSentryEvent = <T extends Event>(event: T): T => {
  try {
    return sentryScrubbers.event(event) as T;
  } catch {
    try {
      return stripEvent(event);
    } catch {
      return { ...pick(event, BARE_EVENT_FIELDS), tags: { ...SCRUB_FAILED_TAG } } as unknown as T;
    }
  }
};

/**
 * beforeBreadcrumb: navigation from/to, fetch and XHR urls, console text. A scrubber
 * failure drops the breadcrumb rather than recording it unscrubbed.
 */
export const scrubSentryBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  try {
    return sentryScrubbers.breadcrumb(breadcrumb);
  } catch {
    return null;
  }
};
